import { useQuery } from '@tanstack/react-query';
import axios from 'axios';

import apiConfig from 'constants/apiConfig';
import { fetchDownloadSignedUrl } from 'components/FileManagement/hooks/useNotebookQueries';

export type FormPhotoFile = {
  id: string;
  fileUrl: string;
  fileName: string | null;
  mimetype: string | null;
  /** Short-lived URL for the thumbnail; null when signing failed. */
  signedUrl: string | null;
};

export const formPhotoFilesKey = (workspaceId: string, ids: string[]) =>
  ['formPhotoFiles', workspaceId, [...ids].sort().join(',')] as const;

/** Resolves the managed files behind a box's photo markers, keyed by file id. */
export const useFormPhotoFiles = (
  workspaceId: string | null | undefined,
  fileIds: string[]
) =>
  useQuery({
    queryKey: formPhotoFilesKey(workspaceId ?? '', fileIds),
    enabled: !!workspaceId && fileIds.length > 0,
    staleTime: 10 * 60 * 1000,
    queryFn: async (): Promise<Map<string, FormPhotoFile>> => {
      const { data } = await axios.get<Omit<FormPhotoFile, 'signedUrl'>[]>(
        `${apiConfig.apiGateway.filesBase}/form-photos`,
        { params: { workspaceId, ids: fileIds.join(',') } }
      );
      const files = await Promise.all(
        data.map(async (file) => ({
          ...file,
          signedUrl: await fetchDownloadSignedUrl(file.fileUrl).catch(
            () => null
          ),
        }))
      );
      return new Map(files.map((file) => [file.id.toLowerCase(), file]));
    },
  });
